import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Truck,
  Languages,
  AlertCircle,
  CheckCircle,
  Globe,
  Package,
  FileCheck,
  Warehouse,
  ArrowRight,
  Shield
} from 'lucide-react';

const BADGE = {
  B1: 'bg-blue-100 text-blue-800 border-blue-300',
  B2: 'bg-blue-100 text-blue-800 border-blue-300',
  C1: 'bg-blue-100 text-blue-800 border-blue-300',
  EN: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const LogisticsCareerPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="logistics-career-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Truck className="w-4 h-4" />
            Career Path
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Logistics &amp; Transportation
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Logistik &amp; Verkehr — Germany's backbone industry, full of <em>Engpassberufe</em> (shortage occupations).
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8">
            <p className="text-babyblue-700 text-lg leading-relaxed mb-4">
              Depending on your position in this sector, German communication can be very crucial — while other roles can be done largely in English.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-900">
                <strong>Engpassberufe</strong> are jobs officially listed by the German government as having severe shortages. This gives applicants priority for work visas and the EU Blue Card.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* German-required jobs */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Jobs Requiring B1 – C1 German
          </h2>
          <p className="text-babyblue-600 text-center mb-8">Strong written and spoken German is non-negotiable.</p>

          <div className="space-y-5">
            {/* Disponenten */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-disponent">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <Package className="w-7 h-7 text-blue-700" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-bold text-babyblue-800">Disponent / Disponentin</h3>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${BADGE.B2}`}>B2 Level</span>
                  </div>
                  <p className="text-babyblue-600 italic text-sm mb-2">Logistics Coordinator &amp; Dispatcher</p>
                  <p className="text-babyblue-700 leading-relaxed">
                    You coordinate routes, drivers, deliveries, and customer communication. Heavy phone &amp; e-mail traffic in German.
                  </p>
                </div>
              </div>
            </div>

            {/* Warehouse Supervisor */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-warehouse">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <Warehouse className="w-7 h-7 text-blue-700" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-bold text-babyblue-800">Lagerleiter / Warehouse Supervisor</h3>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${BADGE.B1}`}>B1+ Level</span>
                  </div>
                  <p className="text-babyblue-600 italic text-sm mb-2">Lagerleiter / Lagerleiterin</p>
                  <p className="text-babyblue-700 leading-relaxed mb-3">
                    Basic everyday German is required, <strong>but</strong> the demanding <em>Arbeitsschutz</em> documentation and incident reports push you to upper-intermediate level.
                  </p>
                  <div className="bg-babyblue-50 border border-babyblue-200 rounded-lg p-3 flex items-start gap-2">
                    <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-babyblue-700">
                      <strong>Arbeitsschutz</strong> covers Germany's health &amp; safety regulations — a legally enforced and very wordy field.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Zollabwicklung */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-customs">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <FileCheck className="w-7 h-7 text-blue-700" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-bold text-babyblue-800">Zollabwicklung</h3>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${BADGE.C1}`}>C1 Level</span>
                  </div>
                  <p className="text-babyblue-600 italic text-sm mb-2">Customs Clearance Specialist</p>
                  <p className="text-babyblue-700 leading-relaxed">
                    You're responsible for import/export laws. Local authorities (<em>Zollamt</em>) require precise German — both written and spoken — and a deep knowledge of legal vocabulary.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* English-friendly jobs */}
      <section className="py-12 px-6 bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Jobs That Hire in English Too
          </h2>
          <p className="text-babyblue-600 text-center mb-8">Lower German barrier — good for newcomers learning the language on the job.</p>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-truck-driver">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-6 h-6 text-emerald-600" />
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${BADGE.EN}`}>EN OK</span>
              </div>
              <h3 className="font-bold text-babyblue-800 mb-1">LKW-Fahrer</h3>
              <p className="text-sm text-babyblue-600 italic mb-2">Truck driver</p>
              <p className="text-sm text-babyblue-700">You drive trucks for importing and exporting goods. A valid EU driving licence (Class C/CE) is required.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-freight">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-6 h-6 text-emerald-600" />
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${BADGE.EN}`}>EN OK</span>
              </div>
              <h3 className="font-bold text-babyblue-800 mb-1">International Freight Forwarder</h3>
              <p className="text-sm text-babyblue-600 italic mb-2">Internationale Spedition</p>
              <p className="text-sm text-babyblue-700">You manage international shipments — English is the working language of global trade.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="job-supply-analyst">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-6 h-6 text-emerald-600" />
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${BADGE.EN}`}>EN OK</span>
              </div>
              <h3 className="font-bold text-babyblue-800 mb-1">Supply Chain Analyst</h3>
              <p className="text-sm text-babyblue-600 italic mb-2">Supply-Chain-Analyst/in</p>
              <p className="text-sm text-babyblue-700">Data-driven role with global teams — typically conducted entirely in English.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pro tip */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <Languages className="w-7 h-7 text-emerald-700 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-emerald-900 mb-2">Pro tip</h3>
                <p className="text-emerald-900 leading-relaxed">
                  Even in English-friendly roles, learning German fast gives you a massive career runway: promotions, internal mobility, and the right to work for any EU employer. Many logistics employers pay for your Integrationskurs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Explore Other Career Paths</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏢', title: 'Business & Office', path: '/careers/business' },
              { icon: '🏥', title: 'Healthcare', path: '/careers/healthcare' },
              { icon: '🍽️', title: 'Hospitality', path: '/careers/hospitality' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LogisticsCareerPage;
